import { FC, useMemo, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
} from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

interface TableFilterDropdownProps {
  selected: string[];
  setSelected: (filters: string[]) => void;
  options: { label: string; value: string }[];
  label?: string;
}

const TableFilterDropdown: FC<TableFilterDropdownProps> = ({
  options,
  selected,
  setSelected,
  label = 'Filter',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (option: string) => {
    const newSelected = [...selected];

    if (selectedSet.has(option)) {
      const index = newSelected.indexOf(option);
      if (index !== -1) {
        newSelected.splice(index, 1);
      }
    } else {
      newSelected.push(option);
    }

    setSelected(newSelected);
  };

  const isOpen = Boolean(anchorEl);
  const menuId = 'filter-menu';

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {selected.length > 0 && (
          <Box
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              bgcolor: '#FF6347',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              {selected.length}
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label={`${label} options`}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          color={selected.length > 0 ? 'primary' : 'default'}
        >
          <FilterAltOutlinedIcon
            sx={{
              fontSize: '1.25rem',
              cursor: 'pointer',
              color: '#162C36',
            }}
          />
        </IconButton>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'filter-button',
        }}
      >
        {options.map(({ label, value }) => (
          <MenuItem key={value} dense>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={selectedSet.has(value)}
                  onChange={() => handleToggle(value)}
                  inputProps={{ 'aria-label': `Filter by ${label}` }}
                />
              }
              label={label}
              sx={{ width: '100%', ml: 0 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TableFilterDropdown;
